// src/client/components/data-display/TasksTable.js
import { formatEmailForTable, formatMoveInDate } from "@/client/utils/tourTableHelpers.js";
import Tooltip from "@/shared/components/feedback/Tooltip.js";
import TextModal from "@/shared/components/modals/TextModal.js";
import {
	CheckCircleIcon as CheckCircleOutlineIcon,
	PencilSquareIcon as PencilSquareOutlineIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon, EnvelopeIcon, PencilSquareIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useState } from "react";

export default function TasksTable({ tasks, forceMobileLayout }) {
	const [messageModal, setMessageModal] = useState({ isOpen: false, message: null });

	// ==================== RENDERING HELPERS ====================
	// All tasks are the current user's own completed tours, so ownership checks are not needed.

	/**
	 * Renders attendance badge next to guest name.
	 *
	 * Only "attended" (green) and "no_show" (red) render a badge. The "unrecorded"
	 * state intentionally renders nothing because every row in this table is by
	 * definition a completed tour awaiting attendance marking — the panel/page
	 * header already states this, so an orange badge on every row would be
	 * redundant noise that experienced agents tune out and that reads poorly in
	 * demos and marketing screenshots (a grid of caution icons signals "problems"
	 * rather than "queue of next actions").
	 *
	 * The green/red badges are kept so that when an agent marks attendance via the
	 * notes modal, they see immediate confirmation in the brief window before the
	 * refetch removes the row from the list.
	 */
	const renderAttendanceBadge = (task) => {
		if (task.attendance === "attended") {
			return (
				<Tooltip text="Attended">
					<span className="ml-1.5 shrink-0">
						<CheckCircleIcon className="h-4 w-4 text-green-500" />
					</span>
				</Tooltip>
			);
		}
		if (task.attendance === "no_show") {
			return (
				<Tooltip text="No show">
					<span className="ml-1.5 shrink-0">
						<XCircleIcon className="h-4 w-4 text-red-500" />
					</span>
				</Tooltip>
			);
		}
		return null;
	};

	/**
	 * Renders notes icon next to guest name (mobile layout only).
	 * All tasks are the user's own, so the icon is always interactive.
	 * Opens TourNotesModal with tourStatus "completed" -- tasks are always completed tours
	 * awaiting attendance marking.
	 * - Notes exist: "View notes" tooltip, click opens TourNotesModal for editing
	 * - No notes: "Add notes" tooltip, click opens TourNotesModal
	 */
	const renderNotesIcon = (task) => {
		const taskPropertySlug = task.property.property_slug;

		if (task.agent_notes) {
			return (
				<Tooltip text="View notes" clickPassthrough>
					<button
						onClick={() =>
							window.openTourNotesModal({
								tourPublicId: task.public_id,
								propertySlug: taskPropertySlug,
							})
						}
						className="ml-1.5 shrink-0"
					>
						<PencilSquareIcon className="h-4 w-4 text-purple hover:text-purple-darker transition-colors" />
					</button>
				</Tooltip>
			);
		}

		return (
			<Tooltip text="Add notes" clickPassthrough>
				<button
					onClick={() =>
						window.openTourNotesModal({
							tourPublicId: task.public_id,
							propertySlug: taskPropertySlug,
						})
					}
					className="ml-1.5 shrink-0"
				>
					<PencilSquareOutlineIcon className="h-4 w-4 text-gray-600 hover:text-purple transition-colors" />
				</button>
			</Tooltip>
		);
	};

	/**
	 * Renders notes button for the desktop Actions column (teal circular button).
	 * All tasks are the user's own, so the button is always interactive.
	 */
	const renderNotesAction = (task) => {
		const taskPropertySlug = task.property.property_slug;

		if (task.agent_notes) {
			return (
				<Tooltip text="View notes" clickPassthrough>
					<button
						onClick={() =>
							window.openTourNotesModal({
								tourPublicId: task.public_id,
								propertySlug: taskPropertySlug,
							})
						}
						className="flex items-center justify-center w-8 h-8 bg-teal-100 hover:bg-teal-200 text-teal-600 hover:text-teal-700 rounded-full transition-colors"
					>
						<PencilSquareIcon className="h-4 w-4" />
					</button>
				</Tooltip>
			);
		}

		return (
			<Tooltip text="Add notes" clickPassthrough>
				<button
					onClick={() =>
						window.openTourNotesModal({
							tourPublicId: task.public_id,
							propertySlug: taskPropertySlug,
						})
					}
					className="flex items-center justify-center w-8 h-8 bg-teal-100 hover:bg-teal-200 text-teal-600 hover:text-teal-700 rounded-full transition-colors"
				>
					<PencilSquareOutlineIcon className="h-4 w-4" />
				</button>
			</Tooltip>
		);
	};

	return (
		<div className="overflow-x-auto">
			{tasks?.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-8 sm:py-16 text-gray-500">
					<CheckCircleOutlineIcon className="h-12 w-12 mb-4 text-gray-300" />
					<p className="text-lg font-medium mb-1">All caught up</p>
					<p className="text-sm">Tours that need attendance marked will appear here.</p>
				</div>
			) : (
				<>
					{/* Mobile View */}
					<div className={forceMobileLayout ? "" : "md:hidden"}>
						{tasks?.map((task, index) => {
							const rowClass = index % 2 === 0 ? "bg-gray-100" : "bg-white";
							const detailParts = [
								task.preferences?.unit_type,
								task.preferences?.move_in_date && `Move-in ${task.preferences.move_in_date}`,
							].filter(Boolean);

							return (
								<div
									key={task.public_id}
									className={`${rowClass} px-2 sm:px-4 py-3 border-b border-gray-200 last:border-b-0`}
								>
									{/* Time · Date + Property badge */}
									<div className="flex flex-wrap items-center gap-1.5">
										<span className="text-xs sm:text-sm font-medium">
											{task.scheduled_time} · {task.scheduled_date}
										</span>
										<span className="text-xs bg-purple/10 text-purple px-2 py-0.5 rounded">{task.property.name}</span>
									</div>
									{/* Guest name + attendance badge + action icons */}
									<div className="flex items-center mt-2">
										<span className="text-sm text-gray-800 min-w-0 truncate">{task.primary_contact.name}</span>
										{renderAttendanceBadge(task)}
										<div className="flex items-center gap-2 ml-2 shrink-0">
											{task.message && (
												<Tooltip text="View guest message" clickPassthrough>
													<button onClick={() => setMessageModal({ isOpen: true, message: task.message })}>
														<EnvelopeIcon className="h-4 w-4 text-gray-500 hover:text-purple transition-colors" />
													</button>
												</Tooltip>
											)}
											{renderNotesIcon(task)}
										</div>
									</div>
									{/* Email and phone */}
									<div className="mt-0.5 text-xs space-y-0.5">
										<div>
											<a href={`mailto:${task.primary_contact.email}`} className="text-purple hover:text-purple-darker">
												{task.primary_contact.email}
											</a>
										</div>
										{task.primary_contact.phone && (
											<div>
												<a href={`tel:${task.primary_contact.phone}`} className="text-purple hover:text-purple-darker">
													{task.primary_contact.phone}
												</a>
											</div>
										)}
									</div>
									{/* Details (unit type, move-in, lead source (with display name)) */}
									{(detailParts.length > 0 || task.lead_source_display_name) && (
										<div className="flex items-center gap-1.5 flex-wrap text-xs text-gray-600 mt-0.5">
											{detailParts.length > 0 && <span>{detailParts.join(" · ")}</span>}
											{task.lead_source_display_name && (
												<span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
													{task.lead_source_display_name}
												</span>
											)}
										</div>
									)}
								</div>
							);
						})}
					</div>

					{/* Desktop View */}
					<table className={`min-w-full table-auto ${forceMobileLayout ? "hidden" : "hidden md:table"}`}>
						<thead>
							<tr>
								<th className="text-left text-base px-4 py-4 font-semibold">Property</th>
								<th className="text-left text-base px-4 py-4 font-semibold">Time</th>
								<th className="text-left text-base px-4 py-4 font-semibold">Guest</th>
								<th className="text-left text-base px-4 py-4 font-semibold">Details</th>
								<th className="text-left text-base px-4 py-4 font-semibold">Actions</th>
							</tr>
						</thead>
						<tbody>
							{tasks?.map((task, index) => (
								<tr key={task.public_id} className={index % 2 === 0 ? "bg-gray-100" : "bg-white"}>
									<td className="px-4 py-4 text-sm">
										<div>
											<div>{task.property.name}</div>
										</div>
									</td>
									{/* whitespace-nowrap prevents date strings like "Feb 16, 2026" from wrapping mid-text at narrow column widths */}
									<td className="px-4 py-4 text-sm whitespace-nowrap">
										<div>{task.scheduled_time}</div>
										<div className="mt-0.5">{task.scheduled_date}</div>
									</td>
									<td className="px-4 py-4 text-sm">
										<div className="flex items-center">
											<span>{task.primary_contact.name}</span>
											{renderAttendanceBadge(task)}
											{task.message && (
												<Tooltip text="View guest message" clickPassthrough>
													<button
														onClick={() => setMessageModal({ isOpen: true, message: task.message })}
														className="ml-2 text-gray-400 hover:text-gray-600"
													>
														<EnvelopeIcon className="h-4 w-4 text-gray-500 hover:text-purple transition-colors" />
													</button>
												</Tooltip>
											)}
										</div>
										<div className="mt-0.5">
											<a
												href={`mailto:${task.primary_contact.email}`}
												className="text-purple hover:text-purple-darker cursor-pointer"
											>
												{formatEmailForTable(task.primary_contact.email)}
											</a>
										</div>
										{task.primary_contact.phone && (
											<div className="mt-0.5">
												<a
													href={`tel:${task.primary_contact.phone}`}
													className="text-purple hover:text-purple-darker cursor-pointer"
												>
													{task.primary_contact.phone}
												</a>
											</div>
										)}
									</td>
									<td className="px-4 py-4 text-sm">
										{task.preferences?.unit_type && <div>{task.preferences.unit_type}</div>}
										{task.preferences?.move_in_date && (
											<div className="text-xs text-gray-600 mt-0.5">
												{formatMoveInDate(task.preferences.move_in_date)}
											</div>
										)}
										{task.lead_source_display_name && (
											<div className="mt-1">
												<span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
													{task.lead_source_display_name}
												</span>
											</div>
										)}
									</td>
									<td className="px-4 py-4 text-sm">
										<div className="flex space-x-2">{renderNotesAction(task)}</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</>
			)}

			{/* Guest Message Modal */}
			<TextModal
				isOpen={messageModal.isOpen}
				onClose={() => setMessageModal({ isOpen: false, message: null })}
				message={messageModal.message}
				title="Guest Message"
			/>
		</div>
	);
}
